interface ProcessStepsPageProps {
  steps: {
    title: string;
    order?: number;
    description: string;
    image?: any;
    imageDataUrl?: string | null;
    _tempId?: string;
  }[];
  title?: string;
  dir?: "rtl" | "ltr";
  emptyLabel?: string;
}

export default function ProcessStepsPage({
  steps,
  dir = "rtl",
  emptyLabel = "-",
}: ProcessStepsPageProps) {
  if (!steps || steps.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#94A3B8",
          fontSize: 16,
          fontWeight: 600,
        }}
      >
        {emptyLabel}
      </div>
    );
  }

  const isRtl = dir === "rtl";

  const useTwoColumns = steps.length > 3;

  const half = useTwoColumns ? Math.ceil(steps.length / 2) : steps.length;
  const firstColumn = steps.slice(0, half);
  const secondColumn = useTwoColumns ? steps.slice(half) : [];

  const columns = isRtl
    ? [firstColumn, secondColumn]
    : [secondColumn, firstColumn];

  const columnStartIndexes = isRtl
    ? [0, firstColumn.length]
    : [secondColumn.length, 0];

  return (
    <div
      dir={dir}
      style={{
        display: "grid",
        gridTemplateColumns: useTwoColumns ? "1fr 1fr" : "1fr",
        gap: 10,
        height: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        alignItems: "stretch",
        boxSizing: "border-box",
        alignContent: "start",
      }}
    >
      {columns.map((columnSteps, colIdx) => {
        if (columnSteps.length === 0) return null;

        return (
          <div
            key={colIdx}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minWidth: 0,
              height: "100%",
              maxHeight: "100%",
              overflow: "hidden",
            }}
          >
            {columnSteps.map((step, rowIdx) => {
              const globalIndex = columnStartIndexes[colIdx] + rowIdx;

              return (
                <div
                  key={step._tempId ?? globalIndex}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                    gap: 10,
                    borderRadius: 14,
                    border: "1px solid #E8EDF5",
                    background:
                      "linear-gradient(145deg, #FFFFFF 0%, #FAFCFE 60%, #F4F8FB 100%)",
                    padding: 8,
                    boxShadow:
                      "0 3px 10px rgba(15,27,51,0.04), 0 1px 2px rgba(15,27,51,0.02)",
                    overflow: "hidden",
                    height: steps.length <= 3 ? 180 : 118,
                    flexShrink: 0,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      bottom: 8,
                      [isRtl ? "right" : "left"]: 0,
                      width: 3,
                      borderRadius: 3,
                      background:
                        "linear-gradient(180deg, #2DA89A 0%, #1A7A76 100%)",
                    }}
                  />

                  <div
                    style={{
                      flex: "0 0 34%",
                      maxWidth: "34%",
                      borderRadius: 8,
                      overflow: "hidden",
                      background:
                        "linear-gradient(145deg, #F8FAFC 0%, #EEF2FF 50%, #E0E7FF 100%)",
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 4,
                      boxSizing: "border-box",
                      position: "relative",
                      minHeight: 0,
                    }}
                  >
                    {step.imageDataUrl ? (
                      <img
                        src={step.imageDataUrl}
                        alt={step.title}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 18, opacity: 0.45 }}>🖼️</span>
                    )}
                  </div>

                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 4,
                      paddingInline: 2,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 6,
                          background:
                            "linear-gradient(135deg, #2DA89A 0%, #1A7A76 100%)",
                          color: "#fff",
                          fontSize: 18,
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 2px 6px rgba(45,168,154,0.3)",
                        }}
                      >
                        {globalIndex + 1}
                      </span>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#0F1B33",
                          lineHeight: 1.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minWidth: 0,
                        }}
                      >
                        {step.title || "-"}
                      </h3>
                    </div>

                    <div
                      style={{
                        height: 1,
                        width: "100%",
                        background:
                          "linear-gradient(90deg, rgba(45,168,154,0.3) 0%, rgba(45,168,154,0) 100%)",
                      }}
                    />

                    <p
                      style={{
                        margin: 0,
                        fontSize: 15,
                        lineHeight: 1.6,
                        color: "#000",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {step.description || "-"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
